import React, { useEffect, useState } from "react";
import { LayoutChangeEvent, Linking, Platform } from "react-native";
import { PdpComponentNames } from "@utils/customization";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import {
  isDiamond,
  isDiamondOrLabGrown,
  isGemstone,
  isJewelry,
  isLabGrown,
  isLinkedPair,
  isPair,
  isParcel,
  replaceDeeplinkScheme,
} from "@utils/product/productValidators";
import {
  containsHTML,
  getProductProperty,
} from "@scenes/findMatch/Utils/ProductDetailList/utils";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import RenderHTML from "react-native-render-html";
import CollapsibleView from "@components/molecules/CollapsibleView";
import SupplierDetail from "@components/molecules/SupplierDetail";
import JewelryConfiguratorComponents from "@scenes/configurator/JewelryConfigurator";
import Personalization from "@scenes/configurator/JewelryConfigurator/Personalization";
import ComponentsSection from "@scenes/pdp/utils/ComponentsSection";
import PairDetailExpand from "@scenes/pdp/utils/PairDetailExpand";
import ProductDetailExpand from "@scenes/pdp/utils/ProductDetailExpand";
import PDPFindMatchButton from "@scenes/pdp/utils/PDPFindMatchButton";
import OwnerInfo from "@scenes/pdp/utils/ownerInfo";
import PriceDisclaimer from "@scenes/pdp/utils/PriceDisclaimer";
import {
  Divider,
  FeatureButtonContainer,
  RefundPolicyContainer,
  SupplierContainer,
  TextContainer,
  ViewContainer,
} from "../utils/pdpComponentStyles";
import { Diamond } from "@src/types/DiamondDetail";
import MatchingBand from "../utils/MatchingBand";
import WebView from "react-native-webview";
import { bodyStyleToCSS } from "@utils/product/productFormatters";
import styled from "styled-components/native";
import { getCommentData } from "../utils/utils";
import InnerHTML from "dangerously-set-html-content";

type UsePdpComponentRendererParams = {
  // common
  componentName: string;
  t: any;
  theme: any;
  typography: any;
  sizes: { spacing: number };
  width: number;
  product_type: any;
  priceMode: number;
  itemDetail: Diamond;
  isModal: boolean;
  isCustomApp: boolean;
  userInfo: any;
  // layouts
  priceDisclaimerLayout: any;
  findMatchSectionLayout: any;
  personalizationSectionLayout: any;
  commentsLayout: any;
  // visibility
  showPersonalizationSection: boolean;
  showCommentSection: boolean;
  showSupplierDetails: boolean;
  shouldShowMatchPairButton: boolean;
  showOwnerInfo: boolean;
  // media/data
  LinkedPairComments: string;
  textComment: string;
  productColor: string;
  // jewelry
  allTypes: any;
  rightComponentWidth: number;
  enterSecondFlow: boolean;
  updateJewelryConfigInfo: (...args: any[]) => void;
  shouldShowJewelryComponents: (itemDetail: any) => boolean;
  onDropdownSelect?: (
    initialValue: string,
    currentValue: string,
    commitFn?: () => void,
    revertFn?: () => void
  ) => void;
  componentsData?: any;
  // refs/handlers
  headerProductView: React.RefObject<any>;
  onFeatureButtonsOffset: (y: number | null) => void;
  handleLayout: (e: any) => void;
  // match
  getMatchPairDetailData: any;
  showMatchingBandSection: any;
  matchingBandData: Diamond[];
  showSupplierSpecificCommentInOrangeMode: boolean;
  refundPolicyLayout: any;
};

type UsePdpComponentRendererProps = {
  renderersProps: any;
};

const INJECT_HEIGHT_JS = `
  (function() {
    var debounceTimer = null;
    function postHeight() {
      var h = document.body.offsetHeight || document.documentElement.offsetHeight;
      if (h > 0) window.ReactNativeWebView.postMessage(String(h));
    }
    function debouncedPostHeight() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(postHeight, 350);
    }
    postHeight();
    window.addEventListener('resize', debouncedPostHeight);
  })();
  true;
`;

const CommentsWebView = ({
  html,
  width,
  theme,
}: {
  html: string;
  width: number;
  theme: any;
}) => {
  const [webViewHeight, setWebViewHeight] = useState(100);
  return (
    <WebViewContainer style={{ height: webViewHeight }}>
      <WebView
        source={{ html }}
        style={[
          { backgroundColor: "transparent" },
          theme.isMobile ? { width } : {},
        ]}
        showsVerticalScrollIndicator={false}
        scrollEnabled={false}
        originWhitelist={["*"]}
        injectedJavaScript={INJECT_HEIGHT_JS}
        onMessage={(event) => {
          const h = parseInt(event.nativeEvent.data, 10);
          if (!isNaN(h) && h > 0) {
            setWebViewHeight((prev) => (Math.abs(prev - h) > 2 ? h : prev));
          }
        }}
        onShouldStartLoadWithRequest={(request) => {
          const url = request.url;
          if (url.startsWith("about:blank")) return true;
          if (url.startsWith("http")) {
            Linking.openURL(url);
            return false;
          }
          return true;
        }}
      />
    </WebViewContainer>
  );
};

const getHtmlText = (
  theme: any,
  typography: any,
  mComments: string,
  color?: string
) => {
  const fontSize = typography["Body-R"]?.fontSize ?? 14;
  const html = `<html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          html, body {
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
            overflow: hidden;
          }
          body {
            ${bodyStyleToCSS(typography["Body-R"])}
            color: ${color ? color : theme["core-05"]};
            font-family: 'Roboto', sans-serif;
            font-size: ${fontSize}px;
            margin: 0;
            padding: 0;
            height: fit-content;
          }
          * {
            font-family: 'Roboto', sans-serif !important;
          }
        </style>
      </head>
      <body>
        <div>
          ${mComments}
        </div>
      </body>
    </html>
`;
  return html;
};

const FeatureButtonsAnchor = ({
  anchorRef,
  onOffset,
  onLayout,
  children,
}: {
  anchorRef: React.RefObject<any>;
  onOffset: (y: number | null) => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  children: React.ReactNode;
}) => {
  useEffect(() => {
    return () => onOffset(null);
  }, [onOffset]);

  return (
    <FeatureButtonContainer
      ref={anchorRef}
      onLayout={(event) => {
        onLayout?.(event);
        onOffset(event.nativeEvent.layout.y);
      }}
    >
      {children}
    </FeatureButtonContainer>
  );
};

export const usePdpComponentRenderer = ({
  renderersProps,
}: UsePdpComponentRendererProps) => {
  return ({
    componentName,
    t,
    theme,
    typography,
    sizes,
    width,
    product_type,
    priceMode,
    itemDetail,
    isModal,
    isCustomApp,
    userInfo,
    priceDisclaimerLayout,
    findMatchSectionLayout,
    personalizationSectionLayout,
    commentsLayout,
    showPersonalizationSection,
    showCommentSection,
    showSupplierDetails,
    shouldShowMatchPairButton,
    showOwnerInfo,
    LinkedPairComments,
    textComment,
    productColor,
    allTypes,
    rightComponentWidth,
    enterSecondFlow,
    updateJewelryConfigInfo,
    shouldShowJewelryComponents,
    onDropdownSelect,
    componentsData,
    headerProductView,
    onFeatureButtonsOffset,
    handleLayout,
    getMatchPairDetailData,
    showMatchingBandSection,
    matchingBandData,
    showSupplierSpecificCommentInOrangeMode,
    refundPolicyLayout,
  }: UsePdpComponentRendererParams) => {
    switch (componentName) {
      case PdpComponentNames.PRICE_DISCLAIMER:
        return (
          <PriceDisclaimer
            itemDetail={itemDetail}
            key={componentName}
            config={priceDisclaimerLayout}
            spacing={sizes.spacing}
            productType={product_type}
            priceMode={priceMode}
          />
        );
      case PdpComponentNames.FEATURE_BUTTONS:
        return !isModal ? (
          <FeatureButtonsAnchor
            key={componentName}
            anchorRef={headerProductView}
            onOffset={onFeatureButtonsOffset}
            onLayout={Platform.OS === "android" ? handleLayout : undefined}
          >
            {shouldShowMatchPairButton &&
              isDiamondOrLabGrown(product_type) &&
              findMatchSectionLayout?.price_modes?.includes(priceMode) &&
              !isPair(itemDetail) &&
              !(isCustomApp && isParcel(itemDetail)) && (
                <>
                  <PDPFindMatchButton
                    buttonLabel={findMatchSectionLayout?.label}
                    buttonIcon={findMatchSectionLayout?.data?.icon}
                    isDiamond={isDiamond(product_type)}
                    isLabgrown={isLabGrown(product_type)}
                    isGemstone={isGemstone(product_type)}
                    isJewelry={isJewelry(product_type)}
                    itemDetail={itemDetail}
                    matchPairDetailData={getMatchPairDetailData}
                    enableMatchPare={
                      !getMatchPairDetailData.isFetching &&
                      getMatchPairDetailData?.data?.data?.diamonds?.length !== 0
                    }
                    productColor={productColor}
                    productType={product_type}
                    priceMode={priceMode}
                  />
                </>
              )}
            {showOwnerInfo && (
              <>
                {shouldShowMatchPairButton &&
                  isDiamondOrLabGrown(product_type) &&
                  findMatchSectionLayout?.price_modes?.includes(priceMode) &&
                  !isPair(itemDetail) &&
                  !isParcel(itemDetail) && <Spacer size={SpaceType.Double} />}
                <OwnerInfo
                  productType={product_type}
                  userInfo={userInfo?.user}
                  itemDetail={itemDetail}
                />
              </>
            )}
          </FeatureButtonsAnchor>
        ) : null;
      case PdpComponentNames.JEWELRY_CONFIGURATOR:
        return itemDetail?.display_configuration_v2 &&
          itemDetail?.custom_design_configuration ? (
          <JewelryConfiguratorComponents
            key={`${componentName}-${priceMode}-${
              userInfo?.user?.locale ?? ""
            }`}
            displayConfigurationV2={allTypes}
            productType={product_type}
            maxWidth={rightComponentWidth}
            enterSecondFlow={enterSecondFlow}
            updateJewelryConfigInfo={updateJewelryConfigInfo}
            onDropdownSelect={onDropdownSelect}
          />
        ) : null;
      case PdpComponentNames.PRODUCT_DETAILS:
        return (
          <ViewContainer key={componentName} isGem={isGemstone(product_type)}>
            {isLinkedPair(itemDetail) ? (
              <PairDetailExpand
                itemDetail={itemDetail}
                title={t("productDetailPage.pairDetails.text", "Pair Details")}
                productColor={productColor}
                isPdpScreen={true}
              />
            ) : (
              <ProductDetailExpand
                itemDetail={itemDetail}
                isProductDetail={true}
                title={
                  isPair(itemDetail)
                    ? t("productDetailPage.pairDetails.text", "Pair Details")
                    : isCustomApp && isParcel(itemDetail)
                    ? t(
                        "productDetailPage.parcelDetails.text",
                        "Parcel Details"
                      )
                    : t(
                        "productDetailPage.productDetails.text",
                        "Product Details"
                      )
                }
                productColor={productColor}
                isPdpScreen={true}
                productType={product_type}
              />
            )}
          </ViewContainer>
        );
      case PdpComponentNames.PERSONALIZATION:
        return showPersonalizationSection ? (
          <Personalization
            key={componentName}
            productType={product_type}
            title={t(personalizationSectionLayout?.label)}
            sub_options={personalizationSectionLayout["sub-sections"]}
            engravable={itemDetail.engravable}
          />
        ) : null;
      case PdpComponentNames.COMPONENTS_SECTION:
        return isJewelry(product_type) &&
          shouldShowJewelryComponents(itemDetail) &&
          componentsData?.data?.components?.length > 0 ? (
          <ComponentsSection
            key={componentName}
            components={componentsData.data.components}
          />
        ) : null;
      case PdpComponentNames.COMMENTS:
        const mContent = isLinkedPair(itemDetail)
          ? LinkedPairComments
          : getCommentData(
              textComment,
              itemDetail,
              priceMode,
              showSupplierSpecificCommentInOrangeMode,
              t
            );

        return (
          Object.keys(commentsLayout)?.length > 0
            ? showCommentSection
            : priceMode === PriceModes.WHITE
        ) ? (
          <ViewContainer key={componentName} isGem={isGemstone(product_type)}>
            <CollapsibleView
              initExpanded={true}
              title={t("productDetailPage.comments.text")}
              arrowStyling={{ color: theme["core-04"] }}
              applyShadow
              alwaysExpanded={userInfo.isIFrame()}
            >
              <Divider />
              <TextContainer>
                {Platform.OS !== "web" && containsHTML(mContent ?? "") ? (
                  <CommentsWebView
                    html={getHtmlText(theme, typography, mContent)}
                    width={width}
                    theme={theme}
                  />
                ) : (
                  <RenderHTML
                    contentWidth={width}
                    defaultTextProps={{ selectable: Platform.OS === "web" }}
                    source={{
                      html: mContent,
                    }}
                    tagsStyles={{ body: typography["Body-R"] }}
                    renderersProps={renderersProps}
                  />
                )}
              </TextContainer>
            </CollapsibleView>
          </ViewContainer>
        ) : null;

      case PdpComponentNames.SUPPLIER_CONTAINER:
        return showSupplierDetails ? (
          <SupplierContainer key={componentName}>
            <SupplierDetail itemDetail={itemDetail} />
          </SupplierContainer>
        ) : null;
      case PdpComponentNames.SUPPLIER_INFO:
        return showSupplierDetails ? (
          <ViewContainer key={componentName} isGem={isGemstone(product_type)}>
            <ProductDetailExpand
              itemDetail={itemDetail}
              productType={product_type}
              isProductDetail={false}
              isPdpScreen={true}
              title={t("productDetailPage.supplierInfo.text")}
              productColor={productColor}
            />
          </ViewContainer>
        ) : null;
      case PdpComponentNames.SUPPLIER_COMMENT:
        let supplierComment = getProductProperty(
          itemDetail?.supplier_specific_comment,
          ""
        );
        if (priceMode === PriceModes.WHITE) {
          const deliveryStr = itemDetail?.estimated_delivery
            ? ` | ${t(
                "productDetailPage.estimatedDelivery.text",
                "Estimated Delivery:"
              )} ${itemDetail.estimated_delivery}`
            : "";
          supplierComment += deliveryStr;
        }
        return itemDetail?.supplier_specific_comment ? (
          <ViewContainer key={componentName} isGem={isGemstone(product_type)}>
            <CollapsibleView
              initExpanded={true}
              title={t("productDetailPage.supplierComments.text")}
              arrowStyling={{ color: theme["core-04"] }}
              alwaysExpanded={userInfo.isIFrame()}
            >
              <Divider />
              <TextContainer>
                <VDBText>{supplierComment}</VDBText>
              </TextContainer>
            </CollapsibleView>
          </ViewContainer>
        ) : null;
      case PdpComponentNames.MATCHING_BAND_DETAILS:
        return showMatchingBandSection?.price_modes?.includes(priceMode) &&
          matchingBandData?.length > 0 ? (
          <MatchingBand
            matchingBandData={matchingBandData}
            productType={product_type}
            priceMode={priceMode}
          />
        ) : null;
      case PdpComponentNames.REFUND_POLICY:
        const handleClick = (e: any) => {
          const link = e.target.closest("a");
          if (!link) return;
          const href = link.getAttribute("href");
          // custom logic
          if (href && Platform.OS !== "web") {
            e.preventDefault();
            const deepLink = replaceDeeplinkScheme(
              href,
              process.env.EXPO_PUBLIC_APP_SCHEME
            );
            Linking.openURL(deepLink);
          } else {
            e.preventDefault();
            Linking.canOpenURL(href).then(() => {
              Linking.openURL(href);
            });
          }
        };

        return refundPolicyLayout?.price_modes?.includes(priceMode) &&
          refundPolicyLayout?.text ? (
          <RefundPolicyContainer>
            {Platform.OS === "web" ? (
              <div onClick={handleClick}>
                <InnerHTML
                  html={t(refundPolicyLayout?.text)}
                  style={{ ...typography["Body-R"], color: theme["core-06"] }}
                />
              </div>
            ) : (
              <CommentsWebView
                html={getHtmlText(
                  theme,
                  typography,
                  t(refundPolicyLayout?.text),
                  theme["core-06"]
                )}
                width={width}
                theme={theme}
              />
            )}
          </RefundPolicyContainer>
        ) : null;
      default:
        return null;
    }
  };
};

const WebViewContainer = styled.View`
  width: 100%;
`;
